import React, { useEffect, useState } from "react";
import { validateLoggedIn, getSession } from "../utils";
import {
    TextField,
    Button,
    Paper,
    CircularProgress,
    Typography,
    Box,
    Grid,
    MenuItem,
} from "@mui/material";
import NavBar from "../navbar";
import { useParams, useNavigate } from "react-router-dom";
import config from "../config";
import * as Sentry from "@sentry/react";
import { formatUtc } from "../utc";

const EditUser = () => {
    const { user_id } = useParams(); // Получение user_id из URL
    const navigate = useNavigate();

    const [userData, setUserData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        const fetchUser = async () => {
            try {
                const response = await fetch(config.apiBaseUrl + `/admin/users?user_id=${user_id}`, {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                        "user-authorization": getSession().token,
                    },
                });
                if (!response.ok) {
                    throw new Error(`Error: ${response.status} ${response.statusText}`);
                }
                const data = await response.json();
                if (!data || data.length === 0) {
                    throw new Error("User data is empty or not found");
                }
                setUserData(data[0]);
            } catch (err) {
                Sentry.captureException(err);
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchUser();
    }, [user_id]);

    const validate_logged_in = validateLoggedIn();
    if (validate_logged_in) {
        return validate_logged_in;
    }

    const handleChange = (field, value) => {
        setUserData({ ...userData, [field]: value });
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            const response = await fetch(config.apiBaseUrl + `/admin/users?user_id=${user_id}`, {
                method: "PUT",
                headers: {
                    "Content-Type": "application/json",
                    "user-authorization": getSession().token,
                },
                body: JSON.stringify(userData),
            });
            if (!response.ok) {
                throw new Error(`Error: ${response.status} ${response.statusText}`);
            }
            alert("User updated successfully!");
            navigate(`/users`);
        } catch (err) {
            Sentry.captureException(err);
            alert(`Failed to save user: ${err.message}`);
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!window.confirm("Are you sure you want to delete this user?")) return;

        try {
            const response = await fetch(config.apiBaseUrl + `/admin/users?user_id=${user_id}`, {
                method: "DELETE",
                headers: {
                    "Content-Type": "application/json",
                    "user-authorization": getSession().token,
                },
            });
            if (!response.ok) {
                throw new Error(`Error: ${response.status} ${response.statusText}`);
            }
            alert("User deleted successfully!");
            navigate(`/users`);
        } catch (err) {
            Sentry.captureException(err);
            alert(`Failed to delete user: ${err.message}`);
        }
    };

    if (loading) {
        return (
            <div style={{ textAlign: "center", padding: "20px" }}>
                <CircularProgress />
            </div>
        );
    }

    if (error) {
        return (
            <div style={{ textAlign: "center", padding: "20px", color: "red" }}>
                <p>{error}</p>
            </div>
        );
    }

    return (
        <>
            <NavBar />
            <Box sx={{ maxWidth: 800, margin: "auto", padding: 3 }}>
                <Paper sx={{ padding: 3 }}>
                    <Typography variant="h5" gutterBottom>
                        Edit User
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                margin="normal"
                                label="ID"
                                value={userData.id || ""}
                                InputProps={{ readOnly: true }}
                            />
                            <TextField
                                fullWidth
                                margin="normal"
                                label="Front ID"
                                value={userData.front_id || ""}
                                InputProps={{ readOnly: true }}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                margin="normal"
                                label="Login"
                                value={userData.login || ""}
                                onChange={(e) => handleChange("login", e.target.value)}
                            />
                            <TextField
                                select
                                fullWidth
                                margin="normal"
                                label="Role"
                                value={userData.role || ""}
                                onChange={(e) => handleChange("role", e.target.value)}
                            >
                                <MenuItem value="member">Member</MenuItem>
                                <MenuItem value="admin">Admin</MenuItem>
                            </TextField>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                margin="normal"
                                label="Email"
                                value={userData.email || ""}
                                onChange={(e) => handleChange("email", e.target.value)}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                margin="normal"
                                label="Name"
                                value={userData.name || ""}
                                onChange={(e) => handleChange("name", e.target.value)}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                margin="normal"
                                label="New Password"
                                type="password"
                                value={userData.password || ""}
                                onChange={(e) => handleChange("password", e.target.value)}
                                helperText="Leave empty to keep current password"
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                select
                                fullWidth
                                margin="normal"
                                label="Email Verified"
                                value={userData.email_verified ? "true" : "false"}
                                onChange={(e) => handleChange("email_verified", e.target.value === "true")}
                            >
                                <MenuItem value="true">Yes</MenuItem>
                                <MenuItem value="false">No</MenuItem>
                            </TextField>
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                margin="normal"
                                label="Stripe Customer ID"
                                value={userData.stripe_customer_id || ""}
                                onChange={(e) => handleChange("stripe_customer_id", e.target.value || null)}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                margin="normal"
                                label="Created At (UTC)"
                                value={formatUtc(userData.created_at, "")}
                                InputProps={{ readOnly: true }}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                margin="normal"
                                label="Modified At (UTC)"
                                value={formatUtc(userData.modified_at, "")}
                                InputProps={{ readOnly: true }}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                margin="normal"
                                label="Last Login At (UTC)"
                                value={formatUtc(userData.last_login_at, "")}
                                InputProps={{ readOnly: true }}
                            />
                        </Grid>
                    </Grid>
                    <Box sx={{ display: "flex", justifyContent: "space-between", marginTop: 3 }}>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleSave}
                            disabled={saving}
                        >
                            {saving ? "Saving..." : "Save"}
                        </Button>
                        <Button
                            variant="outlined"
                            color="error"
                            onClick={handleDelete}
                        >
                            Delete
                        </Button>
                    </Box>
                </Paper>
            </Box>
        </>
    );
};

export default EditUser;
